<template>
  <div>
    <v-container fluid class="py-3">
      <!-- Page Header -->
      <v-row class="mb-3">
        <v-col cols="12">
          <div class="d-flex align-center justify-space-between">
            <div>
              <h2 class="text-h5 font-weight-bold" style="color:#1a1a2e">Company Bank Detail</h2>
              <p class="text-body-2 mb-0 mt-1" style="color:#9aa5b4">Manage your company's primary bank account</p>
            </div>
            <v-btn
              v-if="bankDetail && !isEditMode"
              color="primary"
              elevation="2"
              rounded
              @click="isEditMode = true"
              class="px-6"
            >
              <v-icon left small>mdi-pencil</v-icon>
              Edit Details
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Loading State -->
      <v-row v-if="isLoading">
        <v-col cols="12">
          <v-card elevation="3" rounded="xl" style="min-height: 300px">
            <v-card-text class="d-flex align-center justify-center" style="min-height: 300px">
              <div class="text-center">
                <v-progress-circular indeterminate color="primary" size="60"></v-progress-circular>
                <p class="mt-4 grey--text text-body-1">Loading bank details...</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- View Mode — Bank Detail Found -->
      <v-row v-else-if="bankDetail && !isEditMode">
        <v-col cols="12">
          <v-card elevation="0" rounded="xl" class="bk-detail-card">

            <!-- Hero Banner -->
            <div class="bk-hero">
              <div class="bk-hero-bg"></div>
              <div class="bk-hero-content">
                <div class="d-flex align-center">
                  <div class="bk-bank-avatar mr-5">
                    <v-icon color="white" size="34">mdi-bank</v-icon>
                  </div>
                  <div>
                    <div class="bk-bank-label">Company Bank Account</div>
                    <div class="bk-bank-name">{{ bankDetail.bank_name || 'Bank Account' }}</div>
                    <div class="bk-acct-num">
                      <v-icon size="12" style="opacity:0.7;margin-right:4px">mdi-credit-card-multiple-outline</v-icon>
                      {{ bankDetail.bank_account_number || '—' }}
                    </div>
                  </div>
                </div>
                <div class="bk-hero-badges">
                  <span :class="['bk-badge', bankDetail.is_active ? 'bk-badge--active' : 'bk-badge--inactive']">
                    <v-icon size="11" style="margin-right:3px">{{ bankDetail.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                    {{ bankDetail.is_active ? 'Active' : 'Inactive' }}
                  </span>
                  <span :class="['bk-badge', bankDetail.status ? 'bk-badge--verified' : 'bk-badge--pending']">
                    <v-icon size="11" style="margin-right:3px">{{ bankDetail.status ? 'mdi-shield-check' : 'mdi-shield-alert-outline' }}</v-icon>
                    {{ bankDetail.status ? 'Verified' : 'Pending' }}
                  </span>
                  <span class="bk-badge bk-badge--company">
                    <v-icon size="11" style="margin-right:3px">mdi-domain</v-icon>
                    Company
                  </span>
                </div>
              </div>
            </div>


            <!-- Detail Sections -->
            <div class="bk-body">

              <!-- Account Information -->
              <div class="bk-section-wrap">
                <div class="bk-section-label">
                  <v-icon size="15" color="primary" class="mr-2">mdi-card-account-details-outline</v-icon>
                  Account Information
                </div>
                <div class="bk-fields-grid bk-grid-2">
                  <div class="bk-info-box">
                    <div class="bk-info-label">Account Name</div>
                    <div class="bk-info-value">{{ bankDetail.bank_account_name || '—' }}</div>
                  </div>
                  <div class="bk-info-box">
                    <div class="bk-info-label">Account Number</div>
                    <div class="bk-info-value bk-mono">{{ bankDetail.bank_account_number || '—' }}</div>
                  </div>
                </div>
              </div>

              <div class="bk-section-divider"></div>

              <!-- Bank Information -->
              <div class="bk-section-wrap">
                <div class="bk-section-label">
                  <v-icon size="15" color="primary" class="mr-2">mdi-bank-outline</v-icon>
                  Bank Information
                </div>
                <div class="bk-fields-grid bk-grid-3">
                  <div class="bk-info-box">
                    <div class="bk-info-label">Bank Name</div>
                    <div class="bk-info-value">{{ bankDetail.bank_name || '—' }}</div>
                  </div>
                  <div class="bk-info-box">
                    <div class="bk-info-label">Branch</div>
                    <div class="bk-info-value">{{ bankDetail.bank_branch || '—' }}</div>
                  </div>
                  <div class="bk-info-box">
                    <div class="bk-info-label">Account Type</div>
                    <div class="bk-info-value">{{ bankDetail.account_type ? bankDetail.account_type.description : '—' }}</div>
                  </div>
                  <div class="bk-info-box">
                    <div class="bk-info-label">IFSC Code</div>
                    <div class="bk-info-value bk-mono">
                      <span v-if="bankDetail.ifsc_code" class="bk-code-badge">{{ bankDetail.ifsc_code }}</span>
                      <span v-else>—</span>
                    </div>
                  </div>
                  <div class="bk-info-box">
                    <div class="bk-info-label">MICR Code</div>
                    <div class="bk-info-value bk-mono">
                      <span v-if="bankDetail.micr_code" class="bk-code-badge">{{ bankDetail.micr_code }}</span>
                      <span v-else class="bk-empty">Not set</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="bk-section-divider"></div>

              <!-- UPI Information -->
              <div class="bk-section-wrap">
                <div class="bk-section-label">
                  <v-icon size="15" color="primary" class="mr-2">mdi-cellphone-nfc</v-icon>
                  UPI Information
                </div>
                <div class="bk-fields-grid bk-grid-2 bk-grid-top">
                  <div class="bk-info-box">
                    <div class="bk-info-label">UPI ID</div>
                    <div class="bk-info-value">
                      <span v-if="bankDetail.upi_id" class="bk-upi-id">{{ bankDetail.upi_id }}</span>
                      <span v-else class="bk-empty">Not set</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Edit / Create Form -->
      <v-row v-else-if="isEditMode || !bankDetail">
        <v-col cols="12">
          <v-card elevation="3" rounded="xl">
            <v-card-title class="px-6 pt-5 pb-2">
              <v-icon color="primary" class="mr-2">
                {{ bankDetail ? 'mdi-pencil' : 'mdi-plus-circle' }}
              </v-icon>
              <span class="text-h6 font-weight-bold">
                {{ bankDetail ? 'Edit Company Bank Detail' : 'Add Company Bank Detail' }}
              </span>
            </v-card-title>
            <v-divider class="mx-6"></v-divider>

            <v-card-text class="px-6 py-6">
              <!-- Account Info -->
              <div class="section-header mb-4">
                <v-icon color="primary" class="mr-2">mdi-account-cash</v-icon>
                <span class="text-subtitle-1 font-weight-bold primary--text">Account Information</span>
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    outlined
                    v-model="form.bank_account_name"
                    label="Bank Account Name *"
                    prepend-inner-icon="mdi-account-circle"
                    :error-messages="errors.bank_account_name"
                    maxlength="100"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    outlined
                    v-model="form.bank_account_number"
                    label="Bank Account Number *"
                    prepend-inner-icon="mdi-credit-card"
                    :error-messages="errors.bank_account_number"
                    maxlength="100"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Bank Info -->
              <div class="section-header mb-4 mt-2">
                <v-icon color="primary" class="mr-2">mdi-bank-outline</v-icon>
                <span class="text-subtitle-1 font-weight-bold primary--text">Bank Information</span>
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    outlined
                    v-model="form.bank_name"
                    label="Bank Name"
                    prepend-inner-icon="mdi-bank"
                    :error-messages="errors.bank_name"
                    maxlength="100"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    outlined
                    v-model="form.bank_branch"
                    label="Bank Branch"
                    prepend-inner-icon="mdi-source-branch"
                    :error-messages="errors.bank_branch"
                    maxlength="100"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    outlined
                    v-model="form.account_type_id"
                    :items="accountTypes"
                    item-text="text"
                    item-value="value"
                    label="Account Type"
                    prepend-inner-icon="mdi-file-document"
                    :error-messages="errors.account_type_id"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    outlined
                    v-model="form.ifsc_code"
                    label="IFSC Code"
                    prepend-inner-icon="mdi-identifier"
                    :error-messages="errors.ifsc_code"
                    maxlength="100"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    outlined
                    v-model="form.micr_code"
                    label="MICR Code"
                    prepend-inner-icon="mdi-barcode"
                    :error-messages="errors.micr_code"
                    maxlength="100"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- UPI Info -->
              <div class="section-header mb-4 mt-2">
                <v-icon color="primary" class="mr-2">mdi-cellphone-nfc</v-icon>
                <span class="text-subtitle-1 font-weight-bold primary--text">UPI Information</span>
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    outlined
                    v-model="form.upi_id"
                    label="UPI ID"
                    prepend-inner-icon="mdi-cellphone"
                    :error-messages="errors.upi_id"
                    maxlength="100"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Flags -->
              <v-divider class="my-4"></v-divider>
              <v-row>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="form.is_active"
                    label="Active"
                    color="primary"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="form.status"
                    label="Verified"
                    color="primary"
                  ></v-checkbox>
                </v-col>
              </v-row>

              <!-- Actions -->
              <v-divider class="my-4"></v-divider>
              <v-row>
                <v-col cols="12" class="d-flex justify-center">
                  <v-btn
                    :loading="isSaving"
                    :disabled="isSaving"
                    rounded
                    color="primary"
                    dark
                    class="px-8 mr-3"
                    @click="save"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    {{ bankDetail ? 'Update' : 'Save Bank Detail' }}
                  </v-btn>
                  <v-btn
                    v-if="bankDetail"
                    rounded
                    color="grey"
                    outlined
                    @click="cancelEdit"
                    class="px-6"
                  >
                    Cancel
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Snackbar -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right timeout="3000" rounded="pill">
        <v-icon left>{{ snackbar.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        {{ snackbar.message }}
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: "CompanyBankDetailPage",
  data() {
    return {
      isLoading: false,
      isSaving: false,
      isEditMode: false,
      bankDetail: null,
      errors: {},
      qrCodeFile: null,
      qrCodePreview: null,
      form: {
        user_id: null,
        bank_account_name: "",
        bank_account_number: "",
        bank_name: "",
        bank_branch: "",
        ifsc_code: "",
        micr_code: "",
        account_type_id: null,
        upi_id: "",
        upi_code: "",
        status: false,
        is_active: true,
        is_company: true,
      },
      accountTypes: [],
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  mounted() {
    this.getMasters();
    this.getCompanyBankDetail();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get("masters?context=bank_detail");
        const masters = response.data;
        if (masters.account_types) {
          this.accountTypes = masters.account_types.map((t) => ({
            text: t.description,
            value: t.id,
          }));
        }
      } catch (e) {
        console.error("Failed to load masters", e);
      }
    },

    async getCompanyBankDetail() {
      this.isLoading = true;
      try {
        const response = await axios.get("bank_details?is_company=1&rowsPerPage=1");
        const data = response.data.data || [];
        if (data.length > 0) {
          this.bankDetail = data[0];
          this.populateForm();
        }
      } catch (e) {
        console.error("Failed to fetch company bank detail", e);
      } finally {
        this.isLoading = false;
      }
    },

    populateForm() {
      const b = this.bankDetail;
      this.form = {
        user_id: b.user_id || null,
        bank_account_name: b.bank_account_name || "",
        bank_account_number: b.bank_account_number || "",
        bank_name: b.bank_name || "",
        bank_branch: b.bank_branch || "",
        ifsc_code: b.ifsc_code || "",
        micr_code: b.micr_code || "",
        account_type_id: b.account_type_id || null,
        upi_id: b.upi_id || "",
        upi_code: b.upi_code || "",
        status: b.status || false,
        is_active: b.is_active !== undefined ? b.is_active : true,
        is_company: true,
      };
    },

    cancelEdit() {
      this.isEditMode = false;
      this.errors = {};
      this.qrCodeFile = null;
      this.qrCodePreview = null;
      this.populateForm();
    },

    onQrCodeFileChange(file) {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => { this.qrCodePreview = e.target.result; };
      reader.readAsDataURL(file);
    },

    removeQrCode() {
      this.qrCodeFile = null;
      this.qrCodePreview = null;
    },

    getImageUrl(path) {
      return path && path.startsWith("http") ? path : `${axios.defaults.baseURL}/${path}`;
    },

    async uploadQrCode(bankDetailId) {
      if (!this.qrCodeFile) return;
      const formData = new FormData();
      formData.append("id", bankDetailId);
      formData.append("qr_code_image", this.qrCodeFile);
      await axios.post("/bank_details/upload_qr_code", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },

    async save() {
      this.isSaving = true;
      this.errors = {};

      // Build plain JSON payload (same pattern as id.vue)
      let payload = { ...this.form };
      Object.keys(payload).forEach((key) => {
        if (payload[key] === "" && typeof payload[key] !== "boolean") {
          payload[key] = null;
        }
      });

      try {
        if (this.bankDetail) {
          const encryptedId = this.encryptIt(this.bankDetail.id);
          const response = await axios.patch(`/bank_details/${encryptedId}`, payload);
          if (response.data.success) {
            if (this.qrCodeFile) {
              await this.uploadQrCode(this.bankDetail.id);
            }
          }
        } else {
          const response = await axios.post("/bank_details", payload);
          if (response.data.success && this.qrCodeFile) {
            await this.uploadQrCode(response.data.data.id);
          }
        }
        this.showSnackbar("Bank detail saved successfully!", "success");
        this.isEditMode = false;
        this.qrCodeFile = null;
        this.qrCodePreview = null;
        await this.getCompanyBankDetail();
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.showSnackbar("Failed to save bank detail. Please try again.", "error");
        }
      } finally {
        this.isSaving = false;
      }
    },

    showSnackbar(message, color = "success") {
      this.snackbar = { show: true, message, color };
    },
  },
};
</script>

<style scoped>
/* ════════════════════════════════
   CARD SHELL
════════════════════════════════ */
.bk-detail-card {
  overflow: hidden;
  border: 1px solid #e8edf5 !important;
}

/* ════════════════════════════════
   HERO BANNER
════════════════════════════════ */
.bk-hero {
  position: relative;
  padding: 28px 32px 24px;
  overflow: hidden;
}

.bk-hero-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(130deg, #0d47a1 0%, #1565c0 40%, #1976d2 70%, #42a5f5 100%);
}

.bk-hero-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.bk-bank-avatar {
  width: 68px;
  height: 68px;
  border-radius: 18px;
  background: rgba(255,255,255,0.18);
  border: 2px solid rgba(255,255,255,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  backdrop-filter: blur(6px);
}

.bk-bank-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.65);
  margin-bottom: 4px;
}

.bk-bank-name {
  font-size: 1.35rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.bk-acct-num {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.7);
  margin-top: 5px;
  font-family: "Courier New", monospace;
  letter-spacing: 0.06em;
}

/* Status badges in hero */
.bk-hero-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.bk-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  border: 1.5px solid transparent;
}

.bk-badge--active {
  background: rgba(76, 175, 80, 0.2);
  color: #a5d6a7;
  border-color: rgba(76, 175, 80, 0.4);
}

.bk-badge--inactive {
  background: rgba(244, 67, 54, 0.2);
  color: #ef9a9a;
  border-color: rgba(244, 67, 54, 0.4);
}

.bk-badge--verified {
  background: rgba(0, 188, 212, 0.2);
  color: #80deea;
  border-color: rgba(0, 188, 212, 0.4);
}

.bk-badge--pending {
  background: rgba(255, 152, 0, 0.2);
  color: #ffcc80;
  border-color: rgba(255, 152, 0, 0.4);
}

.bk-badge--company {
  background: rgba(255,255,255,0.15);
  color: rgba(255,255,255,0.85);
  border-color: rgba(255,255,255,0.3);
}

/* ════════════════════════════════
   SUMMARY STRIP
════════════════════════════════ */
.bk-summary-strip {
  display: flex;
  align-items: stretch;
  background: #f8faff;
  border-bottom: 1px solid #e8edf5;
  padding: 0 12px;
}

.bk-strip-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 14px 20px;
  min-width: 0;
}

.bk-strip-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: #9aa5b4;
  margin-bottom: 4px;
}

.bk-strip-value {
  font-size: 0.88rem;
  font-weight: 600;
  color: #2c3e50;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bk-strip-divider {
  width: 1px;
  background: #e0e7ef;
  margin: 10px 0;
}

/* ════════════════════════════════
   DETAIL BODY
════════════════════════════════ */
.bk-body {
  padding: 4px 0 8px;
}

.bk-section-wrap {
  padding: 20px 28px;
}

.bk-section-label {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: #1565c0;
  margin-bottom: 16px;
}

.bk-section-divider {
  height: 1px;
  background: linear-gradient(90deg, #e8edf5 0%, transparent 100%);
  margin: 0 28px;
}

/* Info grid */
.bk-fields-grid {
  display: grid;
  gap: 12px;
}

.bk-grid-2 { grid-template-columns: repeat(2, 1fr); }
.bk-grid-3 { grid-template-columns: repeat(3, 1fr); }
.bk-grid-top { align-items: start; }

@media (max-width: 768px) {
  .bk-grid-2, .bk-grid-3 { grid-template-columns: 1fr; }
  .bk-summary-strip { flex-direction: column; }
  .bk-strip-divider { display: none; }
}

.bk-info-box {
  background: #fff;
  border: 1px solid #edf0f7;
  border-radius: 10px;
  padding: 14px 16px;
  transition: box-shadow 0.15s;
}

.bk-info-box:hover {
  box-shadow: 0 2px 12px rgba(21,101,192,0.08);
}

.bk-info-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9aa5b4;
  margin-bottom: 6px;
}

.bk-info-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a2332;
  line-height: 1.35;
}

.bk-mono { font-family: "Courier New", monospace; letter-spacing: 0.03em; }

.bk-code-badge {
  display: inline-block;
  background: #eef3ff;
  color: #1565c0;
  border: 1px solid #c5d5f7;
  border-radius: 6px;
  padding: 2px 10px;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.bk-upi-id {
  color: #00838f;
  font-weight: 600;
}

.bk-empty {
  color: #b0bec5;
  font-size: 0.85rem;
  font-style: italic;
  font-weight: 400;
}

.bk-qr-img {
  border-radius: 10px;
  border: 1px solid #edf0f7;
}

/* ── Form section headers (edit mode) ── */
.section-header {
  display: flex;
  align-items: center;
}
</style>
